import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Statistic,
  Tag,
  Tooltip,
  message,
} from "antd"
import { useEffect, useState } from "react"
import TagsSelect from "../tags/tags-select-icon"
import moment, { Moment } from "moment-timezone"
import BillableFlag from "components/base/billable-flag"
import { ErrorMessageTooltip } from "components/base/error-message"
import { UserOutlined } from "@ant-design/icons"
import ProjectSelectPill from "../projects/project-select-pill"
import { useTimeLogFormik } from "@timenotes/shared/src/services/time-logs/time-logs.hooks"
import TaskSelectPill from "../tasks/task-select-pill"
import { useCurrentUser } from "hooks/current-user"
import DurationInput from "../time-logs/duration-input"
import TimePicker from "../time-logs/time-picker"
import { useDefaultDateFormat, useDefaultTimeFormat } from "hooks/settings"
import {
  useCurrentProjectsPermissionsQuery,
  usePermissionsQuery,
} from "@timenotes/shared/src/services/permissions/permissions.hooks"
import { getUsersAccountFullName } from "services/users-accounts"
import styled from "styled-components"
import DurationCounter from "../active-tracker/duration-counter"
import { useActiveTrackerQuery } from "@timenotes/shared/src/services/timer/queries/use-active-tracker-query"
import useStopActiveTrackerMutation from "@timenotes/shared/src/services/timer/queries/use-stop-active-tracker-mutation"
import useNoInitialEffect from "hooks/useNoInitialEffect"
import useStartActiveTrackerMutation from "@timenotes/shared/src/services/timer/queries/use-start-active-tracker-mutation"
import { TimeLogFormItemProps } from "@timenotes/shared/src/services/time-logs/time-logs.types"
import {
  TApiTimeLog,
} from "@timenotes/shared/src/services/api-client/types"
import { BulkDateFormFields, getBulkFormFieldInitialValues } from "../time-logs/bulks/forms/bulk-date-form-fields"
import { TeamTargetsFilter } from "../time-logs/team-targets-select"
import UsersAccountSelect from "../users-accounts/users-account-select"
import useUpdateActiveTrackerMutation from "@timenotes/shared/src/services/timer/queries/use-update-active-tracker-mutation"
import delayFunction from "@timenotes/shared/src/services/utils/delay-function"
import { isEqual } from "lodash"
import { ProjectTaskSelectPopover } from "../time-logs/time-log-task-select"

const DATE_TIME_FORMAT = "YYYY-MM-DD"

const DisabledInput = styled(Input)`
  background-color: #fff !important; /* Or whatever your normal input background color is */
  color: #000 !important; /* Or whatever your normal input text color is */
  cursor: not-allowed !important;

  &.ant-input-disabled,
  & .ant-input-disabled {
    background-color: #fff !important; /* Or whatever your normal input background color is */
    color: #000 !important; /* Or whatever your normal input text color is */
    cursor: not-allowed !important;
  }
`

export interface TimeLogFormProps {
  mode: "tracker" | "manual"
  initialValues?: Partial<TimeLogFormItemProps>
  date?: Moment

  enableUsersAccountSelect: boolean
  enableDatePicker?: boolean
  enableBulkMode?: boolean
  onSubmit?(): void
  onSuccess?(timeLog: TApiTimeLog): void
}

const DividerColl = (props: React.ComponentProps<typeof Col>) => (
  <Col
    span="1"
    {...props}
    style={{
      display: "flex",
      justifyContent: "center",
      ...props.style
    }}
  >
    <div
      style={{
        width: "1px",
        height: "40px",
        backgroundColor: "#eee",
      }}
    />
  </Col>
)

const TimeLogForm = (
  props: TimeLogFormProps = {
    mode: "manual",
    enableUsersAccountSelect: false,
    enableBulkMode: false,
  }
) => {
  const {
    enableDatePicker
  } = props

  const trackerQuery = useActiveTrackerQuery({
    options: {
      enabled: props.mode == "tracker",
    },
  })

  const stopTrackerMutation = useStopActiveTrackerMutation({
    options: {
      // @ts-ignore
      onSuccess: (data, variables) => {
        formik.resetForm()
      },
      // @ts-ignore
      onError: (error, variables, context) => {
        if (error && error?.base) {
          message.error(error?.base)
          // Refetching instead of reseting form, as can not be sure for all cases if
          // backend actually stoped the timer?
          formik.resetForm()
          trackerQuery.refetch()
        }
      },
    },
  })
  const startTrackerMutation = useStartActiveTrackerMutation()
  const updateTrackerMutation = useUpdateActiveTrackerMutation()

  const activeTracker = trackerQuery.data?.activeTracker

  useEffect(() => {
    if (activeTracker && props.mode == "tracker") {
      formik.setValues({
        project: activeTracker.project,
        task: activeTracker.task,
        tags: activeTracker.tags,
        description: activeTracker.description,
        isBillable: activeTracker.isBillable,
      })
    }
  }, [activeTracker, props.mode])

  const trackerStartAt = activeTracker
    ? moment.tz(activeTracker.startedAt, activeTracker.timeZone).utc()
    : undefined
  const trackerActive = !!activeTracker && props.mode == "tracker"
  const [currentTime, setCurrentTime] = useState(moment().utc(true))

  const handleStartTracker = () => {
    if (!formik.values.project || !formik.values.task) {
      formik.setErrors({
        project: !!formik.values.project
          ? undefined
          : "Project can't be blank.",
        task: !!formik.values.task ? undefined : "Task can't be blank.",
      })
    } else {
      startTrackerMutation.mutate(formik.values)
    }
  }

  useNoInitialEffect(() => {
    if (startTrackerMutation.isSuccess) {
      // tracker is being filled by react query layer itself
      setCurrentTime(moment().utc(true))
    }

    if (startTrackerMutation.isError) {
      formik.setErrors(startTrackerMutation.error)
    }
  }, [startTrackerMutation.isLoading])

  const handleStopTracker = () => {
    stopTrackerMutation.mutate()
  }

  useEffect(() => {
    const int = setInterval(() => {
      setCurrentTime(moment().utc(true))
    }, 10_000)

    return () => {
      clearInterval(int)
    }
  }, [])

  // TRACKER
  const date =
    moment(props.date).utc().set({ hour: 8, minute: 0, second: 0 }) ||
    moment().utc().set({ hour: 8, minute: 0, second: 0 })

  const { currentUser } = useCurrentUser()

  const permissionsQuery = usePermissionsQuery()

  // TODO: Ideally this should be returned from the backend current project permissions scope
  const canSetTimeLogUser = !!permissionsQuery.data?.permissions.setTimeLogUser
  const canSetTimeLogBillable =
    !!permissionsQuery.data?.permissions.setTimeLogBillable

  const initialUsersAccount = props.initialValues?.usersAccount
    ? props.initialValues.usersAccount
    : currentUser

  const { formik, setNewFromDate, setNewToDate, setNewDuration, endsNextDay } =
    useTimeLogFormik({
      initialValues: {
        duration: 0,
        from: date,
        to: date,
        ...props.initialValues,
        usersAccount: initialUsersAccount,
        ...(props.enableBulkMode ? getBulkFormFieldInitialValues() : {})
      },
      onSuccess: (newTimeLog) => {
        if (props.onSuccess) props.onSuccess(newTimeLog)
      },
    })

  const handleSubmit = () => {
    if (props.onSubmit) props.onSubmit()
    formik.submitForm()
  }

  // For the bulk mode, assign the current user in form if user can not set other users time logs
  useEffect(() => {
    if (!canSetTimeLogUser && props.enableBulkMode) {
      formik.setFieldValue('targets', [{
        id: initialUsersAccount?.id,
        type: 'user',
      }])
    }
  }, [])

  useEffect(() => {
    // Setting up default from - to values should not happen for the tracker mode if trackker running
    if (props.date && !trackerActive) {
      const date = moment(props.date).utc()

      console.error('setting date to', date.format('YYYY-MM-DD HH:mm:ss'))
      formik.setFieldValue("from", date)
      formik.setFieldValue("to", date)
    }
  }, [props.date])

  const { defaultDateFormat } = useDefaultDateFormat()
  const { defaultTimeFormat } = useDefaultTimeFormat()

  const projectPermissionsQuery = useCurrentProjectsPermissionsQuery({
    params: {
      projectId: formik.values.project?.id,
    },
  })
  const isProjectBillableAtAll = !!formik.values.project?.billableEnabled

  const projectTimeLogIsBillableDefault =
    !!formik.values.project?.billableEnabled &&
    !!formik.values.project?.defaultIsBillableTimeLogs

  useEffect(() => {
    if (formik.errors.date) {
      message.error(formik.errors.date)
    }

    if (formik.errors.base) {
      message.error(formik.errors.base)
    }
  }, [formik.errors])

  useNoInitialEffect(() => {
    // Reset is billable after changing the project so latest project settings are reflected
    if (trackerQuery.isFetched && trackerQuery.data?.activeTracker?.id) {
      return
    }
 
    formik.values.isBillable = projectTimeLogIsBillableDefault
  }, [formik.values.project])

  // Is Billable flag setup based on form task selection
  useNoInitialEffect(() => {
    // Do not automatically setup the billable flag if tracker is active and fetched
    if (trackerQuery.isFetched && trackerQuery.data?.activeTracker?.id) {
      return
    }

    // Billable setting based on task selection within a project context
    if (formik.values.task && formik.values.task?.isBillable !== null && formik.values.task?.isBillable !== undefined && formik.values.project && formik.values.project.billableEnabled) {
      formik.values.isBillable = !!formik.values.task.isBillable
    }
    
    // If task is cleared in form, get back to project default billable
    if (!formik.values.task && formik.values.project) {
      formik.values.isBillable = projectTimeLogIsBillableDefault
    }

    if (formik.values.task) {
      const task = formik.values.task

      if (task.tags?.length > 0) {
        // Workaround as #tags might be not an array
        if (Array.isArray(formik.values.tags)) {
          formik.values.tags = [
            ...formik.values.tags,
            ...task.tags.filter(newTag => !formik.values.tags.some(existingTag => existingTag.id === newTag.id))
          ]
        } else {
          formik.values.tags = task.tags
        }
      }
    } else {
      // When task is cleared, remove the tags
      formik.values.tags = []
    }

  }, [formik.values.task?.id])

  // Handle started tracker update logic for formik editable values actually changed
  useNoInitialEffect(() => {
    // For Active Tracker and only when tracker is actually fetched
    if (trackerActive && trackerQuery.status == 'success') {

      // Description update
      if (trackerQuery.data?.activeTracker?.description != formik.values.description) {
        delayFunction('timer-description-update', () => {
          updateTrackerMutation.mutate({
            description: formik.values.description,
          }, {
            onError: () => {
              message.error('Something went wrong.')
            }
          })
        }, 500)
      } // end description update

      // is billable update
      if (trackerQuery.data?.activeTracker?.isBillable != formik.values.isBillable) {
        updateTrackerMutation.mutate({
          isBillable: formik.values.isBillable,
        }, {
          onError: () => {
            message.error('Something went wrong.')
          }
        })
      } // end is billable update

      // tags update
      if (!isEqual(trackerQuery.data?.activeTracker?.tags, formik.values.tags) ) {
        updateTrackerMutation.mutate({
          tags: formik.values.tags,
        }, {
          onError: () => {
            message.error('Something went wrong.')
          }
        })
      } // end tags update

      // start time update
      if (!!trackerStartAt && !!formik.values.from && trackerStartAt?.format() != formik.values.from?.format()) {
        updateTrackerMutation.mutate({
          startedAt: formik.values.from,
        }, {
          onError: () => {
            message.error('Something went wrong.')
          }
        })
      } // end of start time update 

      // start time update
      if (formik.values.task?.id !== trackerQuery.data?.activeTracker?.task.id) {
        updateTrackerMutation.mutate({
          task: formik.values.task,
        }, {
          onError: () => {
            message.error('Something went wrong.')
          }
        })
      } // end of start time update 



    }
  }, [JSON.stringify(formik.values)])


  return (
    <>
      <Form
        onSubmitCapture={(e) => {
          // TODO: Commented to avoid modal portals triggering main time log form submission
          // check if solvable by keeping this later :/
          // formik.submitForm()
        }}
      >
        { !!props.enableBulkMode && (
          <Card>
            <BulkDateFormFields formik={formik} />
          </Card>
        )}
 
        <Card>
          <div style={{ display: "flex", width: "100%", marginBottom: "15px" }}>
            <div style={{ width: formik.values.project ? "" : "100%" }}>
              {trackerActive ? (
                <ProjectTaskSelectPopover
                  project={activeTracker.project}
                  task={activeTracker.task}
                  onTaskChange={(project, task, close) => {
                    formik.setFieldValue('project', project)
                    formik.setFieldValue('task', task)
                    close()
                  }}
                >
                  <ProjectSelectPill
                    value={activeTracker.project}
                    disabled
                  />
                </ProjectTaskSelectPopover>
              ) : (
                <>
                  <ProjectSelectPill
                    disabled={trackerActive}
                    value={formik.values.project}
                    onChange={(project) => formik.setFieldValue("project", project)}
                    onClear={() => {
                      formik.setFieldValue("project", undefined)
                      formik.setFieldValue("task", undefined)
                      formik.setFieldValue("description", undefined)
                    }}
                  />
                  <ErrorMessageTooltip msg={formik.errors.project} />
                </>

              )}
           </div>

            {formik.values.project && (
              <div
                style={{
                  width: formik.values.task ? "auto" : "100%",
                  maxWidth: formik.values.task ? "30%" : "100%",
                  // overflowX: 'hidden' : prevents error message display
                }}
              >
                { trackerActive ? (
                  <ProjectTaskSelectPopover
                    hideProject
                    project={activeTracker.project}
                    task={activeTracker.task}
                    onTaskChange={(project, task, close) => {
                      formik.setFieldValue('project', project)
                      formik.setFieldValue('task', task)
                      close()
                    }}
                    projectSelectProps={{ open: true, defaultOpen: true }}
                  >
                    <div>
                      <TaskSelectPill
                        disabled={trackerActive}
                        project={formik.values.project}
                        value={formik.values.task}
                      />
                    </div>
                  </ProjectTaskSelectPopover>

                ) : (
                  <>
                    <TaskSelectPill
                      disabled={trackerActive}
                      dropdownStyle={{ backgroundColor: "400px " }}
                      project={formik.values.project}
                      defaultOpen={true}
                      value={formik.values.task}
                      onChange={(task) => formik.setFieldValue("task", task)}
                      onClear={() => {
                        formik.setFieldValue("task", undefined)
                        formik.setFieldValue("description", "")
                      }}
                    />
                    <ErrorMessageTooltip
                      msg={formik.errors.task || formik.errors.taskId}
                    />
                  </>
                )}
              </div>
            )}

            {formik.values.project && formik.values.task && (
              <div style={{ width: "100%", marginLeft: "10px" }}>
                <Input
                  placeholder="Description"
                  disabled={false}
                  style={{ width: "100%" }}
                  allowClear={true}
                  autoFocus={true}
                  value={formik.values.description}
                  onChange={(e) => {
                    formik.setFieldValue("description", e.target.value)
                  }}
                />
                <ErrorMessageTooltip msg={formik.errors.description} />
              </div>
            )}
          </div>

          <Row justify="start" align="middle">

            {props.enableUsersAccountSelect ? (
              <>
                {props.enableBulkMode && canSetTimeLogUser ? (
                  <Col span="4">
                    <TeamTargetsFilter
                      placeholder="Members or groups"
                      value={formik.values.targets || []}
                      onChange={(targets) => formik.setFieldValue('targets', targets)}
                    />
                  </Col>
                ) : (
                  <Col span="4">
                    <Tooltip
                      trigger={["hover", "click"]}
                      mouseEnterDelay={0.5}
                      title={
                        !canSetTimeLogUser
                          ? "You have no permissions to log time as other team members."
                          : undefined
                      }
                    >
                      <UsersAccountSelect
                        value={formik.values.usersAccount}
                        autoFocus={false}
                        allowClear={false}
                        disabled={!canSetTimeLogUser}
                        onChange={(newMember) => {
                          formik.setFieldValue("usersAccount", newMember)
                        }}
                      />
                      <ErrorMessageTooltip msg={formik.errors.usersAccount} />
                    </Tooltip>
                  </Col>
                )}
              </>
            ) : (
              <Col span="4" style={{ overflow: "hidden" }}>
                <Tooltip
                  trigger={["hover", "click"]}
                  mouseEnterDelay={0.5}
                  title={
                    canSetTimeLogUser
                      ? "You can not log time as other team member in the timer view. Visit timesheet to manage other team members time entries or reports to bulk update multiple entries at once."
                      : "You have no permissions to manage other team member time entries."
                  }
                >
                  <div>
                    <DisabledInput
                      prefix={<UserOutlined style={{ color: "#ccc" }} />}
                      onKeyDown={(e) => e.preventDefault()}
                      disabled={true}
                      inputMode="search"
                      placeholder={getUsersAccountFullName(initialUsersAccount)}
                    />
                  </div>
                </Tooltip>
                {false && (
                  <Tag style={{ width: "100%" }} icon={<UserOutlined />}>
                    {" "}
                    {initialUsersAccount?.firstName}{" "}
                    {initialUsersAccount?.lastName}{" "}
                  </Tag>
                )}
              </Col>
            )}

            <DividerColl />

            <Col span="1" style={{ justifyContent: "center", display: "flex" }}>
              <TagsSelect
                disabled={false}// trackerActive}
                // @ts-ignore
                value={formik.values.tags || []}
                onChange={(tags) => {
                  formik.setFieldValue("tags", tags)
                }}
              />
              <ErrorMessageTooltip msg={formik.errors.tags} />
            </Col>

            <DividerColl />

            <Col span="1" style={{ display: "flex", justifyContent: "center" }}>
              <BillableFlag
                checked={formik.values.isBillable}
                disabled={!canSetTimeLogBillable || !isProjectBillableAtAll}
                disabledText={
                  !!formik.values.project
                    ? canSetTimeLogBillable
                      ? "This project is not billable."
                      : "You have no permissions to change the time log billable flag."
                    : "Select the project first to fetch it's billable flag settings."
                }
                onChange={() =>
                  formik.setFieldValue("isBillable", !formik.values.isBillable)
                }
              />
              <ErrorMessageTooltip msg={formik.errors.isBillable} />
            </Col>

            <DividerColl />

            {props.mode == "manual" && (
              <>
                <Col span="2">
                  <DurationInput
                    value={formik.values.duration}
                    onChange={(duration) => {
                      setNewDuration(duration)
                    }}
                  />
                  <ErrorMessageTooltip msg={formik.errors.duration} />
                </Col>

                <DividerColl />

                <Col span="2">
                  <TimePicker
                    allowClear={false}
                    style={{ width: "100%" }}
                    value={formik.values.from}
                    onChange={(newVal) => setNewFromDate(newVal as Moment)}
                    format={defaultTimeFormat}
                    suffixIcon={false}
                  />
                  <ErrorMessageTooltip msg={formik.errors.startAt} />
                </Col>

                <Col
                  span={1}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  -
                </Col>

                <Col span="2">
                  <Badge count={endsNextDay ? "+1 day" : undefined}>
                    <TimePicker
                      allowClear={false}
                      style={{ width: "100%" }}
                      value={formik.values.to}
                      onChange={(newVal, newString) => {
                        setNewToDate(newVal as Moment)
                      }}
                      format={defaultTimeFormat}
                      suffixIcon={false}
                    />
                  </Badge>
                </Col>

                <DividerColl />

                {enableDatePicker && (
                  <>
                    <Col span="2">
                      <DatePicker 
                        style={{
                          width: '98px',
                        }}
                        suffixIcon={null}
                        allowClear={false}
                        format={defaultDateFormat}
                        value={formik.values.from}
                        onChange={(newVal) => {
                          setNewFromDate(newVal as Moment, true)
                        }}
                      />
                    </Col>

                    <DividerColl 
                      style={{
                        paddingLeft: '10px',
                      }}
                    />
                  </>
                )}

                <Col 
                  // Adjust button size for datepicker
                  span={enableDatePicker ? 3 : 6}
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      loading={formik.isSubmitting}
                      type="primary"
                      style={{ width: "100%" }}
                      onClick={handleSubmit}
                    >
                      Log time
                    </Button>
                  </div>
                </Col>
              </>
              // END OF MANUAL MODE
            )}

            {props.mode == "tracker" && (
              <>
                <Col
                  span="2"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <DurationCounter
                    cancel={() => handleStopTracker()}
                    active={trackerActive}
                    startedAt={trackerActive ? trackerStartAt : currentTime}
                  />
                </Col>

                <DividerColl />

                <Col
                  span="2"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  { trackerActive ? (
                    <TimePicker
                      allowClear={false}
                      style={{ width: "100%" }}
                      value={trackerStartAt}
                      onChange={(newVal) => {
                        // its purely update active tracker flow
                        if (newVal == null) return

                        const newFrom = moment.utc(newVal.format("YYYY-MM-DD HH:mm"))
                        const currentTime = moment.utc(moment().format("YYYY-MM-DD HH:mm"))

                        if (newFrom > currentTime) {
                          //message.success(`${newVal.format('')} -- ${currentTime.format()}`)
                          message.error("Can not configure timer start time in the future!")
                          return
                        }

                        formik.setFieldValue('from', newFrom)
                      }}
                      format={defaultTimeFormat}
                      suffixIcon={false}
                    />
                  ) : (
                    <Statistic
                      className="timer-period-text"
                      value={(trackerActive
                        ? trackerStartAt
                        : currentTime
                      ).format(defaultTimeFormat)}
                    />

                  )}
                </Col>

                <Col
                  span={1}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  -
                </Col>

                <Col
                  span="2"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Statistic
                    className="timer-period-text"
                    value={currentTime.format(defaultTimeFormat)}
                  />
                </Col>

                <DividerColl />

                <Col span="6">
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {trackerActive ? (
                      <Button
                        loading={
                          formik.isSubmitting || stopTrackerMutation.isLoading
                        }
                        type="primary"
                        danger={true}
                        style={{
                          width: "100%",
                        }}
                        onClick={handleStopTracker}
                      >
                        Stop timer
                      </Button>
                    ) : (
                      <Button
                        loading={
                          formik.isSubmitting || startTrackerMutation.isLoading
                        }
                        type="primary"
                        style={{ width: "100%" }}
                        onClick={handleStartTracker}
                      >
                        Start timer
                      </Button>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Card>

     </Form>
    </>
  )
}

export default TimeLogForm